import { useContext, useState } from 'react';
import { ApiFetcherContext } from 'all-in-one-shared-api';
import FormData from 'form-data';
import useSWR, { Key, SWRConfiguration } from 'swr';

import { FileUploadData, FileUploadResponse, FileUploadResponseObj, ServiceReturnType } from './interfaces';
import { excludeRemovedDocuments } from '../../pages/newClaim/schema';
import { FileUploaded } from '../../components/form';

export const API_PATH = '/xapi/v1/eclaim/document';
interface DocumentTypeValue {
  code: string;
  fileNamePrefix: string;
}
export enum DocumentType {
  RECEIPT = 'RECEIPT',
  REFERRAL_LETTER = 'REFERRAL_LETTER',
  OTHER_DOCUMENT = 'OTHER_DOCUMENT',
  PREV_CLAIM_SETTLEMENT = 'PREV_CLAIM_SETTLEMENT',
}

const getDocumentTypeValues = (documentType: DocumentType): DocumentTypeValue => {
  switch (documentType) {
    case DocumentType.RECEIPT:
      return { code: documentType, fileNamePrefix: 'receipt' };
    case DocumentType.REFERRAL_LETTER:
      return { code: documentType, fileNamePrefix: 'ref-letter' };
    case DocumentType.OTHER_DOCUMENT:
      return { code: documentType, fileNamePrefix: 'other-doc' };
    case DocumentType.PREV_CLAIM_SETTLEMENT:
      return { code: documentType, fileNamePrefix: 'prev-claim' };
  }
};

export const useUploadFiles = (
  key: Key,
  accessToken: string,
  { formData, claimId, treatmentDate }: FileUploadData,
  config?: SWRConfiguration,
): ServiceReturnType<FileUploadResponseObj> => {
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);
  const apiFetcher = useContext(ApiFetcherContext);
  const options = {
    headers: {
      Authorization: 'Bearer ' + accessToken,
      'Content-Type': 'multipart/form-data',
    },
  };

  const uploadFile = async (
    file: File,
    index: number,
    referenceNo: string,
    type: string,
    isLast: boolean,
  ): Promise<FileUploadResponse> => {
    const formDataObj = new FormData();

    formDataObj.append('file', file);

    return await apiFetcher.post<FileUploadResponse>(API_PATH, formDataObj, {
      ...options,
      params: {
        referenceNo,
        type,
        treatmentDate,
        index,
        isLast,
      },
    });
  };
  const uploadFiles = async (
    type: DocumentType,
    files: File[] | undefined,
    referenceNo: string,
  ): Promise<FileUploadResponse[] | null> => {
    if (!files) return null;
    const responses = [];
    const excludedRemovedDocuments = excludeRemovedDocuments(files as FileUploaded[]);
    const acceptanceFiles = excludedRemovedDocuments.filter((file: FileUploaded) => file.type);
    const existingIndexes = excludedRemovedDocuments
      .filter((file: FileUploaded) => !file.type)
      .map((file) => {
        const indexFileSeparator = file.name.indexOf('.');
        return parseInt(file.name.slice(getDocumentTypeValues(type).fileNamePrefix.length, indexFileSeparator));
      });
    const getFileIndex = () => {
      let fileIndex = 1;
      while (existingIndexes.includes(fileIndex)) {
        fileIndex++;
      }
      existingIndexes.push(fileIndex);
      return fileIndex;
    };
    for (let i = 0; i < acceptanceFiles.length; i++) {
      const response = await uploadFile(acceptanceFiles[i], getFileIndex(), referenceNo, type, true);
      responses.push(response);
    }
    return responses;
  };

  const makeRequests = async (): Promise<FileUploadResponseObj> => {
    const receipts = await uploadFiles(DocumentType.RECEIPT, formData['receipts'], claimId);
    const referralLetters = await uploadFiles(DocumentType.REFERRAL_LETTER, formData['referralLetters'], claimId);
    const otherDocuments = await uploadFiles(DocumentType.OTHER_DOCUMENT, formData['otherDocuments'], claimId);
    const prevClaimSettlements = await uploadFiles(
      DocumentType.PREV_CLAIM_SETTLEMENT,
      formData['prevClaimSettlements'],
      claimId,
    );
    return {
      receipts,
      referralLetters,
      otherDocuments,
      prevClaimSettlements,
    };
  };

  const { data, error, isValidating } = useSWR(() => (shouldFetch ? key : null), makeRequests, config);
  if (data) {
    setShouldFetch(false);
  }
  return { data, error, shouldFetch: setShouldFetch, isLoading: isValidating };
};
